import { computed, effect, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';
import { AssignmentWithRelations } from '../../../api/app-types/assignment';
import { ReportDocument, ReportDocumentTemplate } from '../../../api/app-types/report-document.app';
import { User } from '../../../api/app-types/user.app';
import { REPORT_API } from '../../../api/services/report.service';
import { LvrRedirectNav } from '../../../core/classes/redirect-nav.class';
import { AccessService } from '../../services/access.service';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { LOADING } from '../../services/loading.service';
import { LvrFinalizeReportDialog } from '../finalize-report';
import { mergeOriginalTemplateWithEditedContent } from '../html-editor/template.func';
import { splitPathAndQueryParameters } from '../../../shared/helpers/routing.func';
import { REPRESENTATION_MODE } from '../../services/representation-mode.service';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { downloadDocumentFiles } from '../../../shared/helpers/document-download.func';
import { TranslocoService } from '@jsverse/transloco';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';

export class LvrClientReport extends LvrRedirectNav {
	private readonly dialog = inject(MatDialog);
	protected readonly ntfy = inject(NOTIFICATION_SERVICE);
	protected readonly transloco = inject(TranslocoService);
	private readonly access = inject(AccessService);
	private readonly representationMode = inject(REPRESENTATION_MODE);
	private readonly session = inject(SessionManagerService);
	private readonly canEditReportsPermission = hasPermission(this.session.permissions, 'Reports.Edit');
	private readonly templatePage =
		this.resolveDataAsSignal<PaginatedResponse<ReportDocumentTemplate>>('documentTemplatePage');
	private readonly templateEntity = computed(() => {
		const page = this.templatePage();
		return page?.entities[0];
	});
	private readonly prefilledDocumentTemplate = signal<ReportDocumentTemplate | undefined>(undefined);
	protected readonly api = inject(REPORT_API);
	protected readonly loading = inject(LOADING);
	protected readonly clientId = this.resolveRouteParamAsSignal<string | number>('id');
	protected readonly assignmentId = this.resolveDataAsSignal<string | number>('assignmentId');
	protected readonly report = this.resolveDataAsSignal<ReportDocument | undefined>('report');
	protected readonly assignment = this.resolveDataAsSignal<AssignmentWithRelations | undefined>('assignment');
	protected readonly user = this.resolveDataAsSignal<User | undefined>('user');
	public readonly updateHtmlContent = signal<string>('');
	public readonly renderContent = computed(() => {
		if (this.updateHtmlContent()) return this.updateHtmlContent();
		const existingHtml = this.report()?.htmlContent;
		if (existingHtml) return existingHtml;
		const prefilledHtml = this.prefilledDocumentTemplate()?.htmlContent;
		if (prefilledHtml) return prefilledHtml;
		return this.templateEntity()?.htmlContent ?? '';
	});
	public readonly canEditReport = computed(() => {
		if (!this.canEditReportsPermission()) return false;
		if (this.representationMode.isActive()) return true;
		const assignment = this.assignment();
		const user = this.user();
		if (!assignment || !user) return true;
		if (assignment.isAdditionalAssignment) {
			return this.access.isAdditionalOwner(assignment, user);
		}
		return this.access.isMainOwner(assignment, user);
	});
	public readonly canFinalizeReport = computed(() => !this.representationMode.isActive() && this.canEditReport());
	private readonly hasHandledLegacyDownload = signal(false);

	public readonly isExporting = this.loading.isCurrentlyLoading('impediment-report.export', 'requestKey');

	public constructor() {
		super();
		effect(() => {
			const template = this.templateEntity();
			const id = this.assignmentId();
			if (!template || !id) return;

			this.loadPrefilledReport(id, template.id);
		});
		effect(() => {
			const existingReport = this.report();
			if (existingReport) {
				this.updateHtmlContent.set(existingReport.htmlContent);
			}
		});
		effect(() => {
			const existingReport = this.report();
			if (!existingReport || this.hasHandledLegacyDownload()) return;

			const documents = existingReport.documents ?? [];
			if (documents.length < 1) return;

			this.hasHandledLegacyDownload.set(true);
			downloadDocumentFiles(documents);
			this.navigateAfterFinished(this.getAssignmentDetailsCommands(), { relativeTo: this.route, replaceUrl: true });
		});
	}

	public cancel() {
		const redirectUrl = this.redirectUrl();
		if (redirectUrl && !this.shouldIgnoreRedirectUrl(redirectUrl)) {
			this.router.navigateByUrl(redirectUrl);
			return;
		}
		this.router.navigate(this.getAssignmentDetailsCommands(), { relativeTo: this.route });
	}

	public docUpdated(newContent: string) {
		this.updateHtmlContent.set(newContent);
	}

	public async export(fileName: string = 'bericht.pdf') {
		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), 'impediment-report.export');
		try {
			const blob = await this.api.getPdfOfReportDocument(this.assignmentId()!, this.report()!.id);
			const downloadURL = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadURL;
			link.download = fileName;
			link.click();
			setTimeout(() => link.remove(), 200);
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.export_report_error')),
			);
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	public async finalizeReport(baseCommand: string) {
		const report = this.report()!;
		const template = report.template;

		if (!template) return;

		const dialogRef = this.dialog.open(LvrFinalizeReportDialog, {
			maxWidth: '400px',
			data: {
				onConfirm: async () => {
					const content = mergeOriginalTemplateWithEditedContent(report.htmlContent, this.updateHtmlContent());

					await this.api.updateReportDocument({
						assignmentId: this.assignmentId()!,
						htmlContent: content,
						reportId: report.id,
						isFinal: true,
						isDraft: false,
						reportDocumentTemplateId: template.id,
					});

					await this.navigateAfterFinished(this.getAssignmentDetailsCommands(baseCommand), { relativeTo: this.route });

					this.ntfy.addNotification(createSuccessNotification('Bericht erfolgreich abgegeben.'));
				},
				onError: () => {
					this.ntfy.addNotification(createErrorNotification('Fehler beim Abgeben des Berichts.'));
				},
			},
		});

		await lastValueFrom(dialogRef.afterClosed());
	}

	protected getAssignmentDetailsCommands(baseCommand?: string): (string | number)[] {
		const clientId = this.clientId();
		const assignmentId = this.assignmentId();
		if (clientId != null && assignmentId != null) {
			const base = ['/desk', 'client', clientId, 'assignments', assignmentId];
			if (this.assignment()?.isAdditionalAssignment) {
				return [...base, 'additional'];
			}
			return base;
		}
		const base = baseCommand ?? this.getAssignmentDetailsBaseCommand();
		if (this.assignment()?.isAdditionalAssignment) {
			const normalizedBase = base.replace(/\/+$/, '');
			return [`${normalizedBase}/additional`];
		}
		return [base];
	}

	private shouldIgnoreRedirectUrl(redirectUrl: string): boolean {
		if (!this.assignment()?.isAdditionalAssignment) return false;
		const { segments } = splitPathAndQueryParameters(redirectUrl);
		const assignmentIndex = segments.indexOf('assignments');
		if (assignmentIndex === -1) return false;
		const nextSegment = segments[assignmentIndex + 1];
		return nextSegment === 'additional' || nextSegment === 'edit-additional' || nextSegment === 'additional-move';
	}

	private getAssignmentDetailsBaseCommand(): string {
		const segmentCount = this.route.snapshot?.url?.length ?? 3;
		return segmentCount >= 3 ? '../../../' : '../..';
	}

	private async loadPrefilledReport(assignmentId: string | number, templateId: string | number) {
		try {
			const report = await this.api.prefillTemplateForAssignment(assignmentId, templateId);
			this.prefilledDocumentTemplate.set(report);
		} catch {
			this.ntfy.addNotification({
				id: window.crypto.randomUUID(),
				type: 'error',
				message: 'Fehler beim Laden des vorbefüllten Berichts.',
				showToast: true,
				timestamp: new Date(),
			});
		}
	}
}
